/**
 * F1 (integration review 2026-09-26): what Clear finished removes besides the list entries. Main deletes the partial
 * files (`.part`) of an unfinished download, the cleanup pass removes the staged upload copies a file-share upload still
 * owns, and every other unfinished job loses its Resume / Restart / Retry offers. When the finished list holds any of
 * these, the app confirm names them first; otherwise Clear finished clears at once, as before.
 */
import type { TransferJob } from '../../../types/azureStorageTransfers';

/**
 * UI/UX fix round A (U14): Clear finished never discards an unfinished transfer — it removes only jobs with nothing left
 * to resume, restart, retry or clean up (main's `scope: 'finished'`). Discard unfinished… removes the others after the
 * confirmation below (`scope: 'unfinished'`).
 */
export const CLEAR_FINISHED_HINT = 'Removes finished transfers that have nothing left to resume, restart or clean up. Unfinished transfers stay until you discard them.';
export const DISCARD_UNFINISHED_HINT = 'Removes unfinished transfers: their partial download files and staged upload copies are deleted, locks on published files are released, and they can no longer be resumed.';

/** Nothing recoverable: no Resume / Restart / Retry offer and no staged copies left in a share. */
export const isFullyFinished = (job: TransferJob): boolean => job.recovery === 'none' && (job.remoteCleanup?.pending ?? 0) === 0;

export type ClearFinishedLosses = { partialDownloads: number; stagedCopies: number; otherUnfinished: number };

/** Each job counts once: an unfinished download as partial files, else its staged copies, else as another unfinished job. */
export function clearFinishedLosses(finished: readonly TransferJob[]): ClearFinishedLosses {
  const losses: ClearFinishedLosses = { partialDownloads: 0, stagedCopies: 0, otherUnfinished: 0 };
  for (const job of finished) {
    const staged = job.remoteCleanup?.pending ?? 0;
    if (job.kind === 'download' && job.recovery !== 'none') losses.partialDownloads += 1; // a completed job is always 'none'
    else if (staged > 0) losses.stagedCopies += staged;
    else if (job.recovery !== 'none') losses.otherUnfinished += 1;
  }
  return losses;
}

const count = (value: number, one: string, many: string): string => `${value} ${value === 1 ? one : many}`;
const joined = (parts: string[]): string => (parts.length < 2 ? parts.join('') : `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`);

/** The confirmation for `finished`, or undefined when clearing loses nothing recoverable. */
export function clearFinishedConfirmation(finished: readonly TransferJob[]): { title: string; description: string } | undefined {
  const losses = clearFinishedLosses(finished);
  const parts = [
    losses.partialDownloads > 0 ? `deletes any partial files of ${count(losses.partialDownloads, 'unfinished download', 'unfinished downloads')}` : '',
    // UXA-06: what a file-share upload still holds is a staged copy (deleted) or a lock on the published file (released).
    losses.stagedCopies > 0 ? `cleans up ${count(losses.stagedCopies, 'uploaded file', 'uploaded files')} in the file share (deletes their staged copies, releases their locks)` : '',
    losses.otherUnfinished > 0 ? `drops ${count(losses.otherUnfinished, 'other unfinished transfer', 'other unfinished transfers')}` : '',
  ].filter(Boolean);
  if (parts.length === 0) return undefined;
  return { title: 'Discard unfinished transfers?', description: `Discarding ${joined(parts)}. Those transfers can no longer be resumed, restarted or retried.` };
}
